<template>
    <div class="NoPolicy">
        <img :src="iconSrc">
        <div class="NoPolicy__headerText">
            {{ texts.header }}
        </div>
        <div class="NoPolicy__bodyText">
            <div data-test-id="NoPolicy__mainMessage">
                <span>{{ texts.body }}</span>
                <strong v-if="migratedCompliant || isNoRequirementWithRequirementLevelException">manually marked as compliant.</strong>
            </div>
            <div v-if="bodySubText" class="NoPolicy__bodySubText">
                {{ bodySubText }}
            </div>
        </div>
        <RbacDisplayGuard :features="[RbacFeature.entityDetailsAddRemoveException]">
            <NewButton
                v-if="shouldShowGrantException"
                class="NoPolicy__btnGrantException"
                color="primary"
                @click="$emit('grant-exception-for-requirement')"
            >
                Grant Exception for Requirement
            </NewButton>
        </RbacDisplayGuard>
    </div>
</template>

<script lang="ts">
    import { PropType } from 'vue';

    import { Component, Prop, Vue } from '@evidentid/vue-property-decorator';

    import RbacDisplayGuard from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisplayGuard.vue';
    import { ComplianceStatus, ExceptionLevel, VerificationStatus } from '@evidentid/tprm-portal-lib/models/entity-details';
    import { DeclineReason } from '@evidentid/tprm-portal-lib/models/entity-details/DeclineReason.model';
    import {
        EntityRequirementDetails,
    } from '@evidentid/tprm-portal-lib/models/entity-details/EntityRequirement.model';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac' ;

    import NewButton from '@/components/new-button/NewButton.vue';
    import dockInfo from '@/modules/dashboard/assets/doc-info.svg';
    import docClock from '@/modules/dashboard/assets/doc-with-clock.svg';
    import { DECLINE_REASON_TEXTS } from '@/modules/entity-details/components/NoPolicy/config';

    @Component({
        emits: [ 'grant-exception-for-requirement' ],
        components: { NewButton, RbacDisplayGuard },
    })
    export default class NoPolicy extends Vue {
        @Prop({ type: Object as PropType<EntityRequirementDetails>, required: true })
        private requirementDetails!: EntityRequirementDetails;

        @Prop({ type: Boolean, default: false })
        private requirementProcessing!: boolean;

        private pendingIcon = docClock;
        private infoIcon = dockInfo;
        private RbacFeature = RbacFeature;

        private get migratedCompliant(): boolean {
            return this.requirementDetails.complianceStatus === ComplianceStatus.compliant
                && Boolean(this.requirementDetails?.complianceStatusMetadata?.statusOverride?.basedOnMigratedCoverage);
        }

        private get isNoRequirementWithRequirementLevelException(): boolean {
            const isRequirementLevelException = this.requirementDetails?.complianceStatusMetadata?.statusOverride?.level
                === ExceptionLevel.requirementInstance;
            return isRequirementLevelException && !this.requirementDetails.coverage;
        }

        private get texts(): { header: string, body: string } {
            if (this.requirementProcessing) {
                return {
                    header: 'Document Processing',
                    body: 'This document is currently being processed. Its details will be displayed once it\'s been fully evaluated.',
                };
            }

            if (this.migratedCompliant) {
                return {
                    header: 'Migrated Compliance',
                    body: 'This entity has been',
                };
            }

            const declineReasonText = this.getDeclineReasonTexts();

            if (declineReasonText) {
                return declineReasonText;
            }

            if (this.requirementDetails.complianceStatus === ComplianceStatus.pending) {
                return {
                    header: 'Pending Submission',
                    body: 'The entity has been sent a submission link.',
                };
            }

            if (this.isNoRequirementWithRequirementLevelException) {
                return {
                    header: 'Requested Document Not Provided - Exception Granted',
                    body: 'This requirement has been',
                };
            }

            if (this.requirementDetails.verificationStatus === VerificationStatus.noInsuredResponse) {
                return {
                    header: 'Requested Document Not Provided',
                    body: 'This entity did not provide the requested document. Please contact them for more details.',
                };
            }

            return {
                header: 'Requested Document Not Provided',
                body: 'Either a document was not provided or the provided document did not match the requested document. Please contact the entity for more details.',
            };
        }

        private get bodySubText(): string {
            return this.migratedCompliant || this.isNoRequirementWithRequirementLevelException ? 'No submission details have been provided' : '';
        }

        private get iconSrc(): string {
            return this.migratedCompliant ? this.infoIcon : this.pendingIcon;
        }

        private get shouldShowGrantException(): boolean {
            return this.requirementDetails.declineReason === DeclineReason.ExceptionRequested &&
                !this.requirementDetails.complianceStatusMetadata?.statusOverride;
        }

        private getDeclineReasonTexts(): null | { header: string, body: string } {
            if (!this.requirementDetails.declineReason) {
                return null;
            }
            return DECLINE_REASON_TEXTS[this.requirementDetails.declineReason];
        }
    }
</script>
